import { useState } from 'react';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { useGetAgencyMe } from '@/api/generated/agency/agency';
import {
  AgencyMemberDetailSkeleton,
  AgencySelfProfilePage,
  DEFAULT_AGENCY_MEMBER_DETAIL_TAB,
  isAgencySelfProfileTab,
  parseAgencyMemberDetailSearch,
  type AgencyMemberRecord,
  type AgencySelfProfileTab,
} from '@/features/agency';
import { formatDocumentTitle } from '@/lib/document-title';

/**
 * "Your profile" for every active agency member. Admins have one canonical
 * view of themselves — their member detail page with all tabs — so they are
 * redirected there (keeping `?tab=`, incl. Activity); MEMBER-role users, who
 * can't open the Members pages, get the self-profile page.
 */
export const Route = createFileRoute('/_auth/agency/profile')({
  // Full member-detail vocabulary so an admin's `?tab=activity` survives the redirect.
  validateSearch: parseAgencyMemberDetailSearch,
  component: AgencySelfProfileRoute,
});

function AgencySelfProfileRoute() {
  const me = useGetAgencyMe().data?.data ?? null;
  // The agency layout only renders once `/agency/me` resolved; redirect only on data, never mid-load.
  if (!me) return <AgencyMemberDetailSkeleton self />;
  return <AgencySelfProfileGate me={me} />;
}

function AgencySelfProfileGate({ me }: { me: AgencyMemberRecord }) {
  const { tab = DEFAULT_AGENCY_MEMBER_DETAIL_TAB } = Route.useSearch();
  const navigate = Route.useNavigate();
  // Decided once on arrival: a role change picked up by a later `/agency/me`
  // refetch must not yank the page away (and its unsaved-edits guard) mid-edit.
  // Both profile endpoints accept the member themselves, so staying is safe.
  const [redirectAsAdmin] = useState(() => me.role === 'ADMIN');

  if (redirectAsAdmin) {
    return (
      <Navigate
        to="/agency/members/$memberId"
        params={{ memberId: me.id }}
        search={tab === DEFAULT_AGENCY_MEMBER_DETAIL_TAB ? {} : { tab }}
        replace
      />
    );
  }

  const selfTab: AgencySelfProfileTab = isAgencySelfProfileTab(tab) ? tab : DEFAULT_AGENCY_MEMBER_DETAIL_TAB;
  const changeTab = (next: AgencySelfProfileTab) =>
    void navigate({ search: { tab: next === DEFAULT_AGENCY_MEMBER_DETAIL_TAB ? undefined : next } });

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Your profile')}</title>
      </Helmet>
      <AgencySelfProfilePage me={me} tab={selfTab} onTabChange={changeTab} />
    </>
  );
}
